
import Vue from 'vue';
import { ImageItem } from '@gallery/types/Gallery';
import Thumb from '@gallery/components/thumb/Thumb.vue';
import BaseIcon from '@core/components/UI/BaseIcon.vue';

interface Methods {
  scrollIntoView: () => void;
  scrollThumb: (direction: boolean) => void;
}

interface Props {
  pictures: ImageItem[];
  activePictureIndex: number | null;
  isLightboxed: boolean;
  showThumbWrapper: boolean;
}

export default Vue.extend<unknown, Methods, unknown, Props>({
  components: {
    Thumb,
    BaseIcon,
  },
  props: {
    pictures: {
      type: Array,
      required: true,
    },
    activePictureIndex: {
      type: Number,
      required: false,
      default: null,
    },
    isLightboxed: {
      type: Boolean,
      required: false,
    },
    showThumbWrapper: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  watch: {
    activePictureIndex() {
      this.scrollIntoView();
    },
  },
  methods: {
    scrollIntoView() {
      if (typeof this.activePictureIndex !== 'number') {
        return;
      }

      const thumbRow = this.$refs.thumbRow as HTMLDivElement;
      const thumbWidth = thumbRow.children[0].clientWidth + 12; // 6px padding from both sides
      const necessaryChildrenWidthOffest = thumbWidth * this.activePictureIndex;

      // thumbsRow variable can't be used
      // scrollLeft/clientWidth properties will contain incorrect values
      const scrollLeft = (this.$refs.thumbRow as HTMLDivElement).scrollLeft;
      const clientWidth = (this.$refs.thumbRow as HTMLDivElement).clientWidth;

      // This checks if the necessary thumb is in scroll view and negatives result
      if (!(scrollLeft <= necessaryChildrenWidthOffest && necessaryChildrenWidthOffest <= scrollLeft + clientWidth)) {
        (this.$refs.thumbRow as HTMLDivElement).scrollLeft = necessaryChildrenWidthOffest - thumbWidth;
      }
    },
    scrollThumb(direction) {
      const thumbRow = this.$refs.thumbRow as HTMLDivElement;
      const thumbWidth = thumbRow.children[0].clientWidth + 12; // 6px padding from both sides
      const clientWidth = (this.$refs.thumbRow as HTMLDivElement).clientWidth;

      (this.$refs.thumbRow as HTMLDivElement).scrollLeft += direction ? clientWidth - thumbWidth : -(clientWidth - thumbWidth);
    },
  },
  mounted() {
    this.scrollIntoView();

    let pos = { left: 0, x: 0, startTime: 0 };
    const thumbRow = this.$refs.thumbRow as HTMLDivElement;

    const mouseDownHandler = (e: MouseEvent | Event) => {
      // @ts-ignore-next-line
      const touches = e.touches ? e.touches[0] : e;

      thumbRow.style.cursor = 'grabbing';
      thumbRow.style.userSelect = 'none';
      thumbRow.style.scrollBehavior = 'unset';

      pos = {
        // The current scroll
        left: thumbRow.scrollLeft,
        // Get the current mouse position
        x: touches.clientX,
        startTime: Date.now(),
      };

      thumbRow.addEventListener('mousemove', mouseMoveHandler);
      thumbRow.addEventListener('touchmove', mouseMoveHandler);
      thumbRow.addEventListener('mouseleave', mouseUpHandler);
      thumbRow.addEventListener('mouseup', mouseUpHandler);
      thumbRow.addEventListener('touchend', mouseUpHandler);
    };

    const mouseMoveHandler = (e: MouseEvent | Event) => {
      // @ts-ignore-next-line
      const touches = e.touches ? e.touches[0] : e;

      // How far the mouse has been moved
      const dx = touches.clientX - pos.x;

      // Scroll the element
      thumbRow.scrollLeft = pos.left - dx;
    };

    const mouseUpHandler = (e: MouseEvent | Event) => {
      thumbRow.style.cursor = 'grab';
      thumbRow.style.removeProperty('user-select');
      thumbRow.style.scrollBehavior = 'smooth';

      // @ts-ignore-next-line
      const touches = e.touches ? e.touches[0] : e;

      const touchOffset = Math.abs(pos.x - touches.clientX);
      const touchDuration = Date.now() - pos.startTime;

      if (touchDuration < 300 && touchOffset < 25) {
        const paremtEl: HTMLDivElement | undefined = touches.target.parentElement;
        const indexAttribute = paremtEl?.getAttribute('data-index');
        const index = Number(indexAttribute);

        if (index >= 0) {
          this.$emit('toggle-lightbox', index);
        }
      }

      thumbRow.removeEventListener('mousemove', mouseMoveHandler);
      thumbRow.removeEventListener('touchmove', mouseMoveHandler);
      thumbRow.removeEventListener('mouseleave', mouseUpHandler);
      thumbRow.removeEventListener('mouseup', mouseUpHandler);
      thumbRow.removeEventListener('touchend', mouseUpHandler);
    };

    thumbRow.addEventListener('mousedown', mouseDownHandler);
    thumbRow.addEventListener('touchstart', mouseDownHandler);
  },
});
